import React, { useState } from "react"
import { Box, componentStyles } from "@mallardbay/lib-react-components"

import { useTabletMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import type { AmenityFieldsFragment } from "~graphql/generated/graphql"
import {
    AmenityCategory,
    useAllAmenitiesQuery,
} from "~graphql/generated/graphql"
import { TextXLargeBold } from "~components/shared/todo-lib-react-components/text"
import COPY from "~config/copy-constants"
import ShowMoreToggleButton from "~components/shared/show-more-toggle-button"

import { useSharedStyles } from "./use-shared-styles"
import CheckboxGridSection from "./checkbox-grid-section"

interface Props {
    readonly selectedAmenities: string[]
    readonly setSelectedAmenities: (amenities: string[]) => void
}

export default function AmenitySelector({
    selectedAmenities,
    setSelectedAmenities,
}: Props) {
    const sharedStyles = useSharedStyles()
    const styles = useStyleProps()

    const onAmenityChange = ({ id, value }: { id: string; value: boolean }) => {
        if (value) {
            setSelectedAmenities([...selectedAmenities, id])
        } else {
            setSelectedAmenities(
                selectedAmenities.filter((amenityId) => amenityId !== id)
            )
        }
    }

    const {
        isLoading,
        amenitiesToShow,
        includesAmenities,
        featuresAmenities,
        isExpanded,
        setIsExpanded,
    } = useAmenities()

    if (isLoading) return null

    return (
        <Box style={sharedStyles.wrapper}>
            <TextXLargeBold>{COPY.AMENITIES}</TextXLargeBold>
            <Box style={styles.content}>
                <CheckboxGridSection
                    label={COPY.BASIC}
                    items={amenitiesToShow}
                    selectedItems={selectedAmenities}
                    onItemChange={onAmenityChange}
                />
                {isExpanded && (
                    <>
                        <CheckboxGridSection
                            label={COPY.FEATURES}
                            items={featuresAmenities}
                            selectedItems={selectedAmenities}
                            onItemChange={onAmenityChange}
                        />
                        <CheckboxGridSection
                            label={COPY.INCLUDES}
                            items={includesAmenities}
                            selectedItems={selectedAmenities}
                            onItemChange={onAmenityChange}
                        />
                    </>
                )}
                <ShowMoreToggleButton
                    isShowingMore={isExpanded}
                    onToggle={() => setIsExpanded(!isExpanded)}
                />
            </Box>
        </Box>
    )
}

function useStyleProps() {
    return componentStyles({
        content: {
            paddingBottom: "24px",
        },
    })
}

function useAmenities() {
    const [isExpanded, setIsExpanded] = useState(false)

    const { data, loading: isLoading } = useAllAmenitiesQuery()
    const amenities = data?.allAmenities
    const { isLargerThanTablet } = useTabletMediaQueries()

    const basicAmenities = filterAndSortAmenities(
        AmenityCategory.Basic,
        amenities
    )
    const featuresAmenities = filterAndSortAmenities(
        AmenityCategory.Features,
        amenities
    )
    const includesAmenities = filterAndSortAmenities(
        AmenityCategory.Include,
        amenities
    )

    // Amenities to show
    const amenityCountToShowExpanded = basicAmenities.length
    const amenityCountToShowCollapsed = isLargerThanTablet ? 9 : 6
    const amenitiesToShow = basicAmenities.slice(
        0,
        isExpanded ? amenityCountToShowExpanded : amenityCountToShowCollapsed
    )

    return {
        amenitiesToShow,
        featuresAmenities,
        includesAmenities,
        isLoading,
        isExpanded,
        setIsExpanded,
    }
}

function filterAndSortAmenities(
    category: AmenityCategory,
    amenities?: AmenityFieldsFragment[]
) {
    if (!amenities?.length) return []

    return amenities
        .filter((amenity) => amenity.category === category)
        .sort((amenityA, amenityB) => {
            if (amenityA.name > amenityB.name) return 1

            if (amenityA.name < amenityB.name) return -1

            return 0
        })
}
